import { formatDate } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { LocalStorageService, UtilService } from 'library';
import { environment } from 'projects/store/src/environments/environment';
import { LocalStorageKey, Role } from 'projects/store/src/app/enums';
import {
  AdditionalProductFilter,
  AdditionalProductsSalesReportRow,
  ReportColumn,
  SalesAdditionalProductsReportItem,
  UserData
} from 'projects/store/src/app/interfaces';
import {RequestService} from 'projects/store/src/app/services/api/v1/request.service';

type StatusFilterOption = {
  label: string;
  value: string | null;
};

@Component({
  selector: 'app-additional-products-sales-report',
  templateUrl: './additional-products-sales-report.component.html',
  styleUrls: ['./additional-products-sales-report.component.scss']
})
export class AdditionalProductsSalesReportComponent implements OnInit {
  @ViewChild('dt') table?: Table;

  titleBreadcrumbs = 'Reporte de ventas con productos adicionales';
  selectedDate: Date | null = null;
  additionalProductFilter: AdditionalProductFilter = 'all';
  readonly currency = environment.global.country.currency;
  readonly globalFilterFields = ['creditNumber', 'clientFullName', 'brand', 'model', 'productDescription', 'imei'];
  readonly additionalProductFilterOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'Sí', value: 'yes' },
    { label: 'No', value: 'no' }
  ];
  readonly booleanFilterOptions = [
    { label: 'Sí', value: true },
    { label: 'No', value: false }
  ];
  contractStatusOptions: StatusFilterOption[] = this.getDefaultStatusOptions();
  requestStatusOptions: StatusFilterOption[] = this.getDefaultStatusOptions();
  paymentStatusOptions: StatusFilterOption[] = this.getDefaultStatusOptions();
  readonly columns: ReportColumn[] = [
    { field: 'date', header: 'Fecha', type: 'date', minWidth: '8rem', align: 'left' },
    { field: 'creditNumber', header: '# Crédito', type: 'text', minWidth: '9rem', align: 'left', bold: true },
    { field: 'clientFullName', header: 'Cliente', type: 'text', minWidth: '14rem', align: 'left' },
    { field: 'email', header: 'Email', type: 'text', minWidth: '14rem', align: 'left' },
    { field: 'phone', header: 'Teléfono', type: 'text', minWidth: '10rem', align: 'left' },
    { field: 'address', header: 'Dirección', type: 'text', minWidth: '13rem', align: 'left' },
    { field: 'gender', header: 'Género', type: 'text', minWidth: '8rem', align: 'left' },
    { field: 'imei', header: 'IMEI', type: 'text', minWidth: '11rem', align: 'left' },
    { field: 'brand', header: 'Marca', type: 'text', minWidth: '9rem', align: 'left' },
    { field: 'model', header: 'Modelo', type: 'text', minWidth: '12rem', align: 'left' },
    { field: 'productDescription', header: 'Producto', type: 'text', minWidth: '16rem', align: 'left' },
    { field: 'productPrice', header: 'Precio producto', type: 'currency', minWidth: '10rem', align: 'center' },
    { field: 'downPaymentPercentage', header: '% Abono', type: 'percent', minWidth: '8rem', align: 'center' },
    { field: 'downPaymentAmount', header: 'Abono', type: 'currency', minWidth: '9rem', align: 'center' },
    { field: 'amountToFinance', header: 'Monto a financiar', type: 'currency', minWidth: '11rem', align: 'center' },
    { field: 'installmentAmount', header: 'Monto cuota', type: 'currency', minWidth: '10rem', align: 'center' },
    { field: 'installmentCount', header: '# Cuotas', type: 'numeric', minWidth: '8rem', align: 'center' },
    { field: 'paymentFrequency', header: 'Frecuencia pago', type: 'text', minWidth: '11rem', align: 'center' },
    { field: 'ally', header: 'Aliado', type: 'text', minWidth: '11rem', align: 'left', restrictedTo: [Role.commercial_manager] },
    { field: 'store', header: 'Tienda', type: 'text', minWidth: '12rem', align: 'left', restrictedTo: [Role.commercial_manager] },
    { field: 'sellerFirstName', header: 'Nombre vendedor', type: 'text', minWidth: '12rem', align: 'left', restrictedTo: [Role.manager, Role.commercial_manager] },
    { field: 'sellerLastName', header: 'Apellido vendedor', type: 'text', minWidth: '12rem', align: 'left', restrictedTo: [Role.manager, Role.commercial_manager] },
    { field: 'appRegistered', header: 'App registrada', type: 'boolean', minWidth: '10rem', align: 'center' },
    { field: 'contractStatus', header: 'Estado contrato', type: 'status', minWidth: '11.875rem', align: 'center' },
    { field: 'requestStatus', header: 'Estado solicitud', type: 'status', minWidth: '11.875rem', align: 'center' },
    { field: 'paymentStatus', header: 'Estado pago', type: 'status', minWidth: '11.875rem', align: 'center' },
    { field: 'lastConnection', header: 'Última conexión', type: 'datetime', minWidth: '12rem', align: 'left' },
    { field: 'cycledInstallments', header: 'Cuotas cicladas', type: 'numeric', minWidth: '10rem', align: 'left', restrictedTo: [Role.commercial_manager] },
    { field: 'paidInstallments', header: 'Cuotas pagadas', type: 'numeric', minWidth: '10rem', align: 'left', restrictedTo: [Role.commercial_manager] },
    { field: 'overdueInstallments', header: 'Cuotas atrasadas', type: 'numeric', minWidth: '10rem', align: 'left', restrictedTo: [Role.commercial_manager] },
    { field: 'additionalProductName', header: 'Producto adicional', type: 'text', minWidth: '15rem', align: 'left', bold: true, additionalProduct: true },
    { field: 'additionalProductAmount', header: 'Monto producto adicional', type: 'currency', minWidth: '13rem', align: 'center', additionalProduct: true }
  ];
  rows: AdditionalProductsSalesReportRow[] = [];
  filteredRows: AdditionalProductsSalesReportRow[] = [];
  columnFilterValues: Partial<Record<keyof AdditionalProductsSalesReportRow, any>> = {};
  userRole!: Role;

  constructor(private utilService: UtilService,
              private localStorageService: LocalStorageService,
              private requestService: RequestService) {
  }

  ngOnInit() {
    const userData: UserData = this.localStorageService.get(LocalStorageKey.data);
    this.userRole = userData.user.role.code as Role;
    this.loadSalesReport();
    this.utilService.disablePageLoading();
  }

  get visibleColumns(): ReportColumn[] {
    return this.columns.filter(column => this.canViewColumn(column));
  }

  get visibleColumnCount(): number {
    return this.visibleColumns.length;
  }

  get tableMinWidth(): string {
    const columnWidth = this.visibleColumns.reduce((total, column) => total + Number(column.minWidth.replace('rem', '')), 0);
    return `${Math.max(columnWidth, 72)}rem`;
  }

  applyAdditionalProductFilter() {
    this.filteredRows = this.rows.filter(row => {
      const matchesAdditionalProduct = this.additionalProductFilter === 'all'
        || (this.additionalProductFilter === 'yes' && row.hasAdditionalProduct)
        || (this.additionalProductFilter === 'no' && !row.hasAdditionalProduct);

      return matchesAdditionalProduct;
    });
  }

  applyColumnFilter(table: Table, field: keyof AdditionalProductsSalesReportRow, value: any, matchMode: string) {
    const filterValue = value === '' ? null : value;
    this.columnFilterValues[field] = filterValue;
    table.filter(filterValue, field, matchMode);
  }

  applyStartsWithColumnFilter(table: Table, field: keyof AdditionalProductsSalesReportRow, value: string) {
    const filterValue = value === '' ? null : value;
    this.columnFilterValues[field] = filterValue;
    table.filter(filterValue, field, 'startsWith');
  }

  getStatusFilterOptions(column: ReportColumn): StatusFilterOption[] {
    switch (column.field) {
      case 'contractStatus':
        return this.contractStatusOptions;
      case 'requestStatus':
        return this.requestStatusOptions;
      case 'paymentStatus':
        return this.paymentStatusOptions;
      default:
        return this.getDefaultStatusOptions();
    }
  }

  resetColumnFilters() {
    this.columnFilterValues = {};
  }

  clearFilters(table?: Table) {
    this.additionalProductFilter = 'all';
    this.selectedDate = null;
    this.resetColumnFilters();
    (table ?? this.table)?.clear();
    this.loadSalesReport();
  }

  formatBoolean(value: boolean): string {
    return value ? 'Sí' : 'No';
  }

  displayValue(row: AdditionalProductsSalesReportRow, column: ReportColumn): string {
    const value = row[column.field];

    if (value === null || value === undefined || value === '') {
      return '-';
    }

    switch (column.type) {
      case 'date':
        return formatDate(value as Date, 'dd/MM/yyyy', 'es-PA');
      case 'datetime':
        return formatDate(value as Date, 'dd/MM/yyyy HH:mm', 'es-PA');
      case 'currency':
        return `${this.currency} ${Number(value).toLocaleString('es-PA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percent':
        return `${value}%`;
      case 'boolean':
        return this.formatBoolean(Boolean(value));
      default:
        return String(value);
    }
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warning' | 'danger' | 'pending' | 'purple' | 'orange' | undefined {
    switch (status) {
      case 'COMPLETADO':
      case 'Aprobado':
      case 'PAGADO':
      case 'FIRMADO':
        return 'success';
      case 'PENDIENTE':
      case 'En Progreso':
        return 'pending';
      case 'SCORE APROBADO':
        return 'purple';
      case 'RETENIDO':
        return 'orange';
      case 'ANULADO':
      case 'INCOMPLETA':
      case 'CANCELADA':
      case 'RECHAZADA':
        return 'danger';
      default:
        return 'info';
    }
  }

  getAllyPaymentIcon(severity?: string): string {
    switch (severity) {
      case 'success':
        return 'pi pi-check-circle';

      case 'pending':
        return 'pi pi-exclamation-circle';

      case 'danger':
        return 'pi pi-times-circle';

      default:
        return 'pi pi-info-circle';
    }
  }

  private canViewColumn(column: ReportColumn): boolean {
    if (column.additionalProduct && !this.hasVisibleAdditionalProducts()) {
      return false;
    }

    return !column.restrictedTo || column.restrictedTo.includes(this.userRole);
  }

  private hasVisibleAdditionalProducts(): boolean {
    return this.filteredRows.some(row => row.hasAdditionalProduct);
  }

  loadSalesReport() {
    const month = this.selectedDate ? this.selectedDate.getMonth() + 1 : undefined;
    const year = this.selectedDate ? this.selectedDate.getFullYear() : undefined;

    this.requestService.getSalesReport(month, year).subscribe({
      next: response => {
        const list = response.body?.data?.list ?? [];
        this.rows = list.map((item: SalesAdditionalProductsReportItem) => this.mapSalesReportItemToRow(item));
        this.updateStatusFilterOptions();
        this.applyAdditionalProductFilter();
      },
      error: () => {
        this.rows = [];
        this.filteredRows = [];
        this.updateStatusFilterOptions();
      }
    });
  }

  private updateStatusFilterOptions() {
    this.contractStatusOptions = this.getStatusOptions('contractStatus');
    this.requestStatusOptions = this.getStatusOptions('requestStatus');
    this.paymentStatusOptions = this.getStatusOptions('paymentStatus');
  }

  private getStatusOptions(field: keyof AdditionalProductsSalesReportRow): StatusFilterOption[] {
    const uniqueStatuses = Array.from(new Set(
      this.rows
        .map(row => row[field])
        .filter((status): status is string => typeof status === 'string' && status.trim() !== '')
    ));

    return [
      ...this.getDefaultStatusOptions(),
      ...uniqueStatuses.map(status => ({ label: status, value: status }))
    ];
  }

  private getDefaultStatusOptions(): StatusFilterOption[] {
    return [{ label: 'Todos', value: null }];
  }

  private mapSalesReportItemToRow(item: SalesAdditionalProductsReportItem): AdditionalProductsSalesReportRow {
    const additionalProduct = item.additional_product;

    return {
      date: new Date(item.sale.date),
      creditNumber: item.sale.credit_number,
      clientFullName: `${item.client.first_name ?? ''} ${item.client.last_name ?? ''}`.trim(),
      email: item.client.email ?? '',
      phone: item.client.phone ?? '',
      address: item.client.address ?? '',
      gender: item.client.gender ?? '',
      imei: item.product.imei,
      brand: item.product.brand,
      model: item.product.model,
      productDescription: item.product.description,
      productPrice: item.product.price,
      downPaymentPercentage: item.sale.down_payment_pct,
      downPaymentAmount: item.sale.down_payment,
      amountToFinance: item.sale.financed_amount,
      installmentAmount: item.sale.installment_amount,
      installmentCount: item.sale.installment_count,
      paymentFrequency: item.sale.payment_frequency,
      ally: item.seller.dealer_name,
      store: item.seller.store_name,
      sellerFirstName: item.seller.first_name,
      sellerLastName: item.seller.last_name,
      appRegistered: item.sale.registered_app,
      contractStatus: item.sale.contract_status,
      requestStatus: item.sale.request_status,
      paymentStatus: item.sale.payment_status,
      lastConnection: item.sale.last_connection ? new Date(item.sale.last_connection) : null,
      cycledInstallments: item.installment.cycled_installments,
      paidInstallments: item.installment.paid_installments,
      overdueInstallments: item.installment.overdue_installments,
      hasAdditionalProduct: Boolean(additionalProduct),
      additionalProductName: additionalProduct?.name ?? null,
      additionalProductAmount: additionalProduct?.amount ?? null
    };
  }
}
